import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Facebook, RefreshCw, Send, Clock, CheckCircle, XCircle, AlertCircle, Copy, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getFacebookPreviewApi, postFacebookNowApi, getFacebookHistoryApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const SCHEDULE_HOURS = ['6 AM', '9 AM', '12 PM', '3 PM', '6 PM', '9 PM'];

export default function FacebookAutoPost() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const { data } = await getFacebookPreviewApi();
      setPreview(data);
    } catch (e) {
      toast.error('Failed to load preview');
    }
    setLoading(false);
  };

  const fetchHistory = async () => {
    try {
      const { data } = await getFacebookHistoryApi();
      setHistory(data.posts || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    fetchPreview();
    fetchHistory();
  }, []);

  const handlePostNow = async () => {
    if (!preview?.configured) {
      toast.error('Facebook credentials not set. Add FB_PAGE_ID and FB_PAGE_ACCESS_TOKEN to backend/.env');
      return;
    }
    if (!preview?.windows?.length) {
      toast.error('Nothing to post — no available windows today.');
      return;
    }
    if (!window.confirm('Post this flyer to your Facebook Page now?')) return;
    setPosting(true);
    try {
      const { data } = await postFacebookNowApi();
      toast.success(`Posted! ${data.window_count} window(s) shared.`);
      await fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Posting failed');
    }
    setPosting(false);
  };

  const handleCopyCaption = () => {
    if (!preview?.caption) return;
    navigator.clipboard.writeText(preview.caption);
    toast.success('Caption copied to clipboard');
  };

  return (
    <div className="min-h-screen bg-page-bg">
      {/* Header */}
      <div className="bg-hero-bg text-white">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/operator/admin')}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <p className="text-xs uppercase tracking-widest text-brand-teal mb-1">MARKETING</p>
              <h1 className="text-2xl font-bold">Facebook Auto-Post</h1>
            </div>
          </div>
          <Facebook className="w-7 h-7 text-brand-teal" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Status bar */}
        <div className="bg-white rounded-2xl border border-divider p-5 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${preview?.configured ? 'bg-open-play animate-pulse' : 'bg-status-held'}`} />
            <span className="text-sm font-medium text-text-primary">
              {preview?.configured ? 'Auto-post is LIVE' : 'Auto-post not configured'}
            </span>
          </div>
          {!preview?.configured && (
            <span className="text-xs text-text-secondary">
              Add <code className="bg-page-bg px-1.5 py-0.5 rounded text-status-held">FB_PAGE_ID</code> and{' '}
              <code className="bg-page-bg px-1.5 py-0.5 rounded text-status-held">FB_PAGE_ACCESS_TOKEN</code> to backend/.env
            </span>
          )}
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <Clock className="w-3.5 h-3.5" />
            Posts at: {SCHEDULE_HOURS.join(' · ')} PHT
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-8">
          {/* Left: flyer preview */}
          <div className="md:col-span-3 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wider">Today's flyer</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchPreview}
                disabled={loading}
                data-testid="refresh-preview-btn"
                className="text-brand-teal hover:bg-brand-teal-tint"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            <div className="bg-white rounded-2xl border border-divider overflow-hidden shadow-sm">
              {loading ? (
                <div className="aspect-square flex items-center justify-center text-text-secondary">
                  <RefreshCw className="w-6 h-6 animate-spin" />
                </div>
              ) : preview?.image_base64 ? (
                <img
                  src={preview.image_base64}
                  alt="Today's Facebook flyer"
                  className="w-full block"
                  data-testid="flyer-preview-image"
                />
              ) : (
                <div className="aspect-square flex items-center justify-center text-text-secondary">
                  No preview available
                </div>
              )}
            </div>

            {preview && !preview.windows?.length && (
              <div className="bg-status-held-bg border border-status-held/30 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-status-held shrink-0 mt-0.5" />
                <div className="text-sm text-status-held">
                  <p className="font-medium">No availability detected for the rest of today.</p>
                  <p className="mt-1 text-status-held/80">Auto-post will skip this slot. Nothing will be posted.</p>
                </div>
              </div>
            )}
          </div>

          {/* Right: caption + action */}
          <div className="md:col-span-2 space-y-4">
            <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wider">Caption</h2>
            <div className="bg-white rounded-2xl border border-divider p-5 relative">
              <pre className="whitespace-pre-wrap text-sm text-text-primary font-sans leading-relaxed" data-testid="caption-text">
                {preview?.caption || 'Loading...'}
              </pre>
              <button
                onClick={handleCopyCaption}
                className="absolute top-3 right-3 p-2 rounded-lg hover:bg-page-bg text-text-secondary hover:text-brand-teal transition-colors"
                title="Copy caption"
                data-testid="copy-caption-btn"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>

            <Button
              onClick={handlePostNow}
              disabled={posting || loading || !preview?.windows?.length || !preview?.configured}
              className="w-full rounded-full py-6 text-base font-semibold text-white"
              style={{ backgroundColor: '#0F8F8D' }}
              data-testid="post-now-btn"
            >
              {posting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Post to Facebook Now
                </>
              )}
            </Button>

            <p className="text-xs text-text-secondary text-center">
              Posted by <span className="font-medium">{user?.name || user?.email}</span> · 
              Logs to history below
            </p>
          </div>
        </div>

        {/* Post history */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wider">Recent posts</h2>
          <div className="bg-white rounded-2xl border border-divider overflow-hidden">
            {history.length === 0 ? (
              <div className="p-8 text-center text-sm text-text-secondary">
                No posts yet. Click "Post to Facebook Now" to send your first flyer.
              </div>
            ) : (
              <div className="divide-y divide-divider">
                {history.map((p, i) => (
                  <div key={i} className="p-4 flex items-center gap-4" data-testid={`history-row-${i}`}>
                    {p.status === 'success' && <CheckCircle className="w-5 h-5 text-open-play shrink-0" />}
                    {p.status === 'error' && <XCircle className="w-5 h-5 text-blocks shrink-0" />}
                    {p.status === 'skipped_no_availability' && <AlertCircle className="w-5 h-5 text-status-held shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-text-primary">
                          {new Date(p.created_at).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' })}
                        </span>
                        <Badge variant="outline" className={
                          p.trigger === 'manual'
                            ? 'border-brand-teal/30 text-brand-teal text-[10px]'
                            : 'border-status-review/30 text-status-review text-[10px]'
                        }>
                          {p.trigger}
                        </Badge>
                      </div>
                      <p className="text-xs text-text-secondary mt-0.5">
                        {p.status === 'success' && `${p.window_count} window(s) posted`}
                        {p.status === 'error' && (p.error || 'Failed')}
                        {p.status === 'skipped_no_availability' && 'Skipped — fully booked'}
                      </p>
                    </div>
                    {p.photo_id && (
                      <a
                        href={`https://www.facebook.com/${p.photo_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-brand-teal hover:underline flex items-center gap-1"
                      >
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Schedule info card */}
        <div className="bg-brand-teal-tint rounded-2xl p-5 border border-brand-teal/20">
          <h3 className="text-sm font-semibold text-brand-teal mb-2">How it works</h3>
          <ul className="text-sm text-brand-teal/80 space-y-1.5">
            <li>• Auto-post fires at <strong>6 AM, 9 AM, 12 PM, 3 PM, 6 PM, 9 PM</strong> PHT</li>
            <li>• Only posts contiguous time windows with at least 1 court available</li>
            <li>• Skips silently when fully booked — no "we're full" spam</li>
            <li>• Use "Post Now" for ad-hoc shares (e.g., after a cancellation opens a slot)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
